import type {ReactNode} from "react";
import {AlertDialog} from "@/primitives/AlertDialog";
import {Button} from "@/primitives/Button";
import {FA6Icon} from "@/primitives/FA6Icon";

interface ConfirmDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	message: string;
	/** The confirming button(s); the dialog adds the declining action itself. */
	actions: ReactNode;
	/**
	 * Declining label — name what stays unchanged rather than "Cancel" where that reads better.
	 * It is the emphasised button: declining is the safe outcome of a confirmation.
	 */
	cancelLabel?: string;
	cancelIcon?: string;
}

/**
 * Confirmation dialog for an action that cannot be undone. Every way out — the ✕, a backdrop
 * click, Escape, and the declining button — leaves the action undone, so all of them are enabled.
 */
export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	message,
	actions,
	cancelLabel = messages["dialog.cancel"],
	cancelIcon = "xmark",
}: ConfirmDialogProps) {
	return (
		<AlertDialog.Root open={open} onOpenChange={onOpenChange}>
			<AlertDialog.Content variation="warning" size="md" showCloseIcon backdropClose escapeClose>
				<AlertDialog.Header>
					<AlertDialog.Title>{title}</AlertDialog.Title>
				</AlertDialog.Header>
				<AlertDialog.Body>
					<AlertDialog.Description>{message}</AlertDialog.Description>
				</AlertDialog.Body>
				<AlertDialog.Footer>
					{actions}
					<AlertDialog.Close render={<Button type="button"/>}>
						<FA6Icon icon={cancelIcon} variant="regular" fixedWidth className="mr-5"/>
						{cancelLabel}
					</AlertDialog.Close>
				</AlertDialog.Footer>
			</AlertDialog.Content>
		</AlertDialog.Root>
	);
}
